import {useWebsocketTokenMap} from "../classes/WebsocketTokenMap.js";

export default class {
    constructor() {
        this.callbackIndex = 0;
        this.callbacks = {};
        this.callbackSubscribedIndex = 0;
        this.callbacksSubscribed = {};
        this.WebsocketTokenMap = useWebsocketTokenMap();
        this.subscribed = null;
    }

    install(app, option) {
        this.app = app;

        this.startListening();

        const exposed = {
            registerCallback: this.registerCallback.bind(this),
            registerSubscribedCallback: this.registerSubscribedCallback.bind(this),
            removeCallback: this.removeCallback.bind(this),
            removeSubscribedCallback: this.removeSubscribedCallback.bind(this),
            postMessage: this.postMessage.bind(this),
            isSubscribed: this.isSubscribed.bind(this)
        };

        /**
         * Example:
         * this.$root.$reverb
         */
        app.config.globalProperties.$reverb = exposed;

        /**
         * Example:
         * import { inject } from 'vue';
         *
         * const Reverb = inject('Reverb')
         */
        app.provide('Reverb', exposed);
    }

    getPrivateChannelKey() {
        return 'User.' + this.app.config.globalProperties.$user.data.id;
    }

    getPrivateChannel() {
        return Echo.private(this.getPrivateChannelKey());
    }

    /**
     * @returns {null|Boolean}
     */
    isSubscribed() {
        return this.subscribed;
    }

    startListening() {
        if(this.app.config.globalProperties.$user.isLoggedIn()) {
            let $this = this;
            this.getPrivateChannel()
                .subscribed(() => {
                    $this.subscribed = true;
                    for(let index in this.callbacksSubscribed) {
                        this.callbacksSubscribed[index]();
                        this.removeSubscribedCallback(index);
                    }
                })
                .error(() => {
                    $this.subscribed = false;
                    console.error('Error when connecting to the private channel');
                })
                .listenToAll(this.eventHandler.bind(this));
        }
    }

    eventHandler(name, data) {
        Object.values(this.callbacks).forEach((entry) => {
            if(entry.type === name || entry.type === '*') {
                entry.callback(data);
            }
        });
    }

    /**
     * Use this function to register callbacks to execute when subscription to the private channel was successful.
     * Will get executed only once when the connection is established.
     * Similar to the setTimeout() function where the delay value is the moment of the connection.
     *
     * @param {Function} callback Callback function at the event
     * @returns {Number} callback id
     */
    registerSubscribedCallback(callback) {
        if(this.subscribed) {
            console.error('Already registered to the channel. Registering new callbacks will not have any effect as they will not get called anymore!');
            return 0;
        }

        this.callbacksSubscribed[++this.callbackSubscribedIndex] = callback;

        return this.callbackSubscribedIndex;
    }

    /**
     * @param {Number} id
     * @returns {this}
     */
    removeSubscribedCallback(id) {
        delete(this.callbacksSubscribed[id]);

        return this;
    }

    /**
     * Use this function to register callbacks to execute every time when a broadcast messages has been received.
     *
     * @param {String} type Serverside broadcast event name or "*" for anything
     * @param {Function} callback Callback function at the event
     * @returns {Number} callback id
     */
    registerCallback(type, callback) {
        this.callbacks[++this.callbackIndex] = {
            type: type,
            callback: callback
        };

        return this.callbackIndex;
    }

    /**
     * @param {Number} id
     * @returns {this}
     */
    removeCallback(id) {
        delete(this.callbacks[id]);

        return this;
    }

    /**
     * Use this method to push a message to the server.
     *
     * @file node_modules/laravel-echo/src/channel/pusher-private-channel.ts
     * @param {String} type Event type. Will get prefixed by "client-" by the Echo framework!
     * @param {any} data Stringifyable data
     */
    postMessage(type, data) {
        this.getPrivateChannel().whisper(type, {
            token: this.WebsocketTokenMap.get('private-' + this.getPrivateChannelKey()) ?? '',
            user_id: this.app.config.globalProperties.$user.data.id,
            data: data
        });
    }
}
